import { getSignInState } from '@/api/widget/signin'
import { getWidgetSettingsAPI } from '@/api/widget/settings'
import { getWidgetConfig } from '@/config'
import { RecommendationType, WidgetSettings, WidgetStep } from '@/types'
import {
  EcomLocation,
  LocationsResponse,
  LoyaltyLocation,
  MenuType,
  SignInStateResponse,
  SubEcomLocation,
} from '@/types/widget'
import { widgetBridge } from '@/bridge'
import { showErrorToast as errorToast, logger } from '@/utils'
import {
  IDENTITY_CONTACT_EVENT,
  persistSharedContactId,
} from '@/utils/contactIdentity'
import { checkUrlTargets } from '@/utils/checkUrlTargets'
import { UrlMatcher } from '@/utils/urlMatcher'
import { resolveCurrentEcomProvider } from '@/utils/resolveEcomProviders'
import { createPersistOptions, sessionContext, widgetSettingsCache } from '@/utils/zustand-persist'
import { getDefaultWidgetSettings } from '@/utils/widget-utils'
import merge from 'lodash.merge'
import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import {
  doesEcomLocationBelongToUID,
  getEcomLocationByGlobalID,
  getLocationsResponseData,
  getStoreIDByGlobalID,
} from './useLocationsStore'
import { useChatStore } from './useChatStore'
import { useCompletedReviewsStore } from './useCompletedReviewsStore'
import { usePendingReviewsStore } from './usePendingReviewsStore'
import { useRecommendationStore } from './useRecommendationStore'
import { useRewardsStore } from './useRewardsStore'
import { useSubmitReviewStore } from './useSubmitReviewStore'
import { useWidgetSignInFormStore } from './useWidgetSignInFormStore'
import { useFlowState } from './useFlowState'
import { useWidgetStore } from './useWidgetStore'

type SessionStoreState = {
  initialUIDs: string[]
  currentUID: string | undefined
  currentGlobalID: string | undefined
  currentMenuType: MenuType | undefined
  contactID: string | undefined
  ecomMenuProvider: string | undefined
  widgetSettings: WidgetSettings | undefined
  signInState: SignInStateResponse | undefined
  /**
   * JWT issued by `POST /widget/reviews/auth/verify/:uid/:pii`. Kept
   * in-memory only (not persisted) so a full page reload forces re-auth.
   * That is the conservative choice for a token that can read review
   * history. The token itself has a 24hr TTL server-side.
   */
  reviewToken: string | undefined
  reviewTokenExpiresAt: string | undefined
  /**
   * The raw phone or email the shopper typed during OTP. Sent as the `:pii`
   * URL segment on all subsequent review calls; server normalizes and
   * matches against the JWT identity claim.
   */
  reviewPII: string | undefined
}

type SessionStoreActions = {
  initialize: (data: LocationsResponse) => Promise<void>
  setInitialUIDs: () => string[] | undefined
  setCurrentUID: (uid: string) => void
  setCurrentGlobalID: (globalID: string, menuTypeHint?: MenuType) => void
  setCurrentMenuType: (menuType: MenuType) => void
  setContactID: (contactID: string | undefined, source?: 'widget' | 'bridge') => void
  setReviewAuth: (auth: { token: string; expiresAt: string; pii: string }) => void
  clearReviewAuth: () => void
  fetchWidgetSettings: (
    uid?: string,
    globalID?: string,
    menuType?: MenuType
  ) => Promise<WidgetSettings | undefined>
  fetchSignInState: (uid?: string) => Promise<SignInStateResponse | undefined>
}

type SessionStore = SessionStoreState & SessionStoreActions

const initialState: SessionStoreState = {
  initialUIDs: [],
  currentUID: undefined,
  currentGlobalID: undefined,
  currentMenuType: undefined,
  contactID: undefined,
  ecomMenuProvider: undefined,
  widgetSettings: undefined,
  signInState: undefined,
  reviewToken: undefined,
  reviewTokenExpiresAt: undefined,
  reviewPII: undefined,
}

function resetUIDDependentStores() {
  usePendingReviewsStore.getState().reset()
  useCompletedReviewsStore.getState().reset()
  useRecommendationStore.getState().resetRequest()
  useRewardsStore.getState().reset()
  useSubmitReviewStore.getState().reset()
  useWidgetSignInFormStore.getState().reset()
  useChatStore.getState().reset()
  // Review JWT is scoped to a (uid, pii) pair; drop it when the UID or the
  // active store changes so the next review-flow entry re-authenticates.
  useSessionStore.getState().clearReviewAuth()
}

export const useSessionStore = create<SessionStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        initialize: async (data: LocationsResponse = getLocationsResponseData()) => {
          const { ecomLocations, loyaltyLocations } = data

          const initialUIDs = get().initialUIDs
          const embeddedConfig = getWidgetConfig()
          const currentContext = {
            uids: [...initialUIDs].sort(),
            configGlobalID: embeddedConfig?.globalID,
          }

          const storedContext = sessionContext.get()
          if (storedContext) {
            const uidsChanged =
              JSON.stringify(storedContext.uids) !== JSON.stringify(currentContext.uids)
            const configGlobalIDChanged =
              storedContext.configGlobalID !== currentContext.configGlobalID

            if (uidsChanged) {
              set(
                {
                  currentUID: undefined,
                  currentGlobalID: undefined,
                  currentMenuType: undefined,
                  ecomMenuProvider: undefined,
                },
                false,
                'initialize/sessionContextChanged'
              )
              // Also clear aiq_contact_id so hydrate can't restore the prior merchant's contact.
              get().setContactID(undefined)
            } else if (configGlobalIDChanged && currentContext.configGlobalID) {
              set(
                {
                  currentGlobalID: undefined,
                  currentMenuType: undefined,
                  ecomMenuProvider: undefined,
                },
                false,
                'initialize/configGlobalIDChanged'
              )
            }
          }

          sessionContext.set(currentContext)

          const persistedGlobalID = get().currentGlobalID
          if (persistedGlobalID) {
            const persistedLocation = getEcomLocationByGlobalID(persistedGlobalID, ecomLocations ?? [])
            if (!persistedLocation) {
              set(
                { currentGlobalID: undefined, currentMenuType: undefined },
                false,
                'initialize/clearStaleGlobalID'
              )
            } else {
              const persistedMenuType = get().currentMenuType
              if (
                persistedMenuType &&
                !persistedLocation.subEcomLocations.some((sub) => sub.menuType === persistedMenuType)
              ) {
                set({ currentMenuType: undefined }, false, 'initialize/clearStaleMenuType')
              }
            }
          }

          const bestMatch = findBestMatchingGlobalID(ecomLocations ?? [])
          if (bestMatch) {
            const { globalID, uid, menuType } = bestMatch
            const persistedGlobalID = get().currentGlobalID
            const persistedMenuType = get().currentMenuType
            const isStoreChange = persistedGlobalID !== undefined && persistedGlobalID !== globalID
            const isMenuTypeChange =
              persistedMenuType !== undefined && persistedMenuType !== menuType
            if (isStoreChange || isMenuTypeChange) {
              resetUIDDependentStores()
              useFlowState.getState().setCurrentStep(WidgetStep.Welcome)
              useFlowState.getState().setCurrentFlow(undefined)
            }
            const provider = resolveCurrentEcomProvider(data, globalID, menuType, uid)
            set(
              {
                currentGlobalID: globalID,
                currentUID: uid,
                currentMenuType: menuType,
                ecomMenuProvider: provider,
              },
              false,
              'initialize/setFromGlobalID'
            )
            await get().fetchWidgetSettings(uid, globalID, menuType)
            get().fetchSignInState(uid)
            useRewardsStore.getState().fetchRecReward()
            return
          }

          const matchingUIDs = checkForMatchingUID(loyaltyLocations ?? [])
          if (matchingUIDs.size === 1) {
            const matchingUID = matchingUIDs.keys().next().value
            const provider = resolveCurrentEcomProvider(data, undefined, undefined, matchingUID)
            set({ currentUID: matchingUID, ecomMenuProvider: provider }, false, 'initialize/setFromUID')
            await get().fetchWidgetSettings(matchingUID)
            get().fetchSignInState(matchingUID)
            useRewardsStore.getState().fetchRecReward()
            return
          }

          if (initialUIDs.length === 1) {
            const uid = initialUIDs[0]
            set({ currentUID: uid }, false, 'initialize/fallbackUID')
            await get().fetchWidgetSettings(uid)
            get().fetchSignInState(uid)
            useRewardsStore.getState().fetchRecReward()
          }
        },

        setInitialUIDs: () => {
          const embeddedConfig = getWidgetConfig()
          let uids: string[] = []

          if (embeddedConfig?.uids && embeddedConfig.uids.length > 0) {
            uids = embeddedConfig.uids
          } else {
            const queryParams = new URLSearchParams(window.location.search)
            uids = (
              String(queryParams.get('uids') ?? '')
                ?.trim()
                ?.split(',') ?? []
            )?.filter((uid) => uid.trim() !== '')
          }

          logger.log('setInitialUIDs', { uids })
          if (uids.length > 0) {
            set({ initialUIDs: uids }, false, 'setInitialUIDs')
            return uids
          }
          logger.error('setInitialUIDs', 'No UID provided')
          return undefined
        },

        setCurrentUID: (uid: string) => {
          set({ currentUID: uid }, false, 'setCurrentUID')
          logger.log('setCurrentUID', { uid })
          resetUIDDependentStores()

          const data = getLocationsResponseData()
          const globalID = get().currentGlobalID
          if (globalID && !doesEcomLocationBelongToUID(globalID, uid)) {
            const bestMatch = findBestMatchingGlobalID(data?.ecomLocations ?? [], uid)
            if (bestMatch?.globalID && bestMatch?.menuType) {
              set(
                { currentGlobalID: bestMatch.globalID, currentMenuType: bestMatch.menuType },
                false,
                'setCurrentUID/updateGlobalID'
              )
              logger.log('setCurrentUID', { globalID: bestMatch.globalID, menuType: bestMatch.menuType })
            }
          }

          const { currentGlobalID, currentMenuType } = get()
          const provider = resolveCurrentEcomProvider(data, currentGlobalID, currentMenuType, uid)
          set({ ecomMenuProvider: provider }, false, 'setCurrentUID/updateEcomProvider')

          get().fetchWidgetSettings(uid)
          get().fetchSignInState(uid)
          useRewardsStore.getState().fetchRecReward()
        },

        setCurrentGlobalID: (globalID: string, menuTypeHint?: MenuType) => {
          const location = getEcomLocationByGlobalID(globalID)

          if (!location) {
            return errorToast(`Location with globalID ${globalID} not found`)
          }

          const previousGlobalID = get().currentGlobalID
          const isStoreChange = previousGlobalID !== undefined && previousGlobalID !== globalID

          const currentUID = get().currentUID
          const uidChanged = !currentUID || !doesEcomLocationBelongToUID(globalID, currentUID)
          if (uidChanged) {
            if (location) {
              set(
                { currentUID: location.uid, signInState: undefined },
                false,
                'setCurrentGlobalID/updateUID'
              )
              // Also clear aiq_contact_id so hydrate can't restore the prior merchant's contact.
              get().setContactID(undefined)
              logger.log('setCurrentGlobalID', { currentUID: location.uid })
              resetUIDDependentStores()
            }
          }

          const subEcomLocations = location.subEcomLocations
          const validHint = menuTypeHint && subEcomLocations.some((sub) => sub.menuType === menuTypeHint)
          if (subEcomLocations.length === 1) {
            set({ currentMenuType: subEcomLocations[0].menuType }, false, 'setCurrentGlobalID/setMenuType')
            logger.log('setCurrentGlobalID', { currentMenuType: subEcomLocations[0].menuType })
          } else if (validHint) {
            set({ currentMenuType: menuTypeHint }, false, 'setCurrentGlobalID/setMenuTypeFromHint')
            logger.log('setCurrentGlobalID', { currentMenuType: menuTypeHint })
          } else {
            set({ currentMenuType: undefined }, false, 'setCurrentGlobalID/clearMenuType')
          }

          set({ currentGlobalID: globalID }, false, 'setCurrentGlobalID')
          logger.log('setCurrentGlobalID', { globalID })

          if (isStoreChange) {
            resetUIDDependentStores()
            useFlowState.getState().setCurrentStep(WidgetStep.Welcome)
            useFlowState.getState().setCurrentFlow(undefined)
          }

          const data = getLocationsResponseData()
          const { currentMenuType } = get()
          const effectiveUID = uidChanged && location ? location.uid : currentUID
          const provider = resolveCurrentEcomProvider(data, globalID, currentMenuType, effectiveUID)
          set({ ecomMenuProvider: provider }, false, 'setCurrentGlobalID/updateEcomProvider')

          get().fetchWidgetSettings(effectiveUID)

          if (uidChanged && location) {
            get().fetchSignInState(location.uid)
          }
        },

        setCurrentMenuType: (menuType: MenuType) => {
          const currentGlobalID = get().currentGlobalID
          if (!currentGlobalID) {
            errorToast('No current globalID set')
            return undefined
          }
          const location = getEcomLocationByGlobalID(currentGlobalID)
          if (!location) {
            errorToast(`Location with globalID ${currentGlobalID} not found`)
            return undefined
          }
          if (!location.subEcomLocations.some((subEcomLocation) => subEcomLocation.menuType === menuType)) {
            errorToast(`Menu type ${menuType} not found for globalID ${currentGlobalID}`)
            return undefined
          }

          const previousMenuType = get().currentMenuType
          const isMenuTypeChange = previousMenuType !== undefined && previousMenuType !== menuType

          set({ currentMenuType: menuType }, false, 'setCurrentMenuType')
          logger.log('setCurrentMenuType', { menuType })

          if (isMenuTypeChange) {
            resetUIDDependentStores()
            useFlowState.getState().setCurrentStep(WidgetStep.Welcome)
            useFlowState.getState().setCurrentFlow(undefined)
          }

          const data = getLocationsResponseData()
          const currentUID = get().currentUID
          const provider = resolveCurrentEcomProvider(data, currentGlobalID, menuType, currentUID)
          set({ ecomMenuProvider: provider }, false, 'setCurrentMenuType/updateEcomProvider')

          if (isMenuTypeChange) {
            get().fetchWidgetSettings(currentUID, currentGlobalID, menuType)
          }

          return menuType
        },

        setContactID: (contactID: string | undefined, source: 'widget' | 'bridge' = 'widget') => {
          const trimmed = typeof contactID === 'string' ? contactID.trim() : undefined
          const next = trimmed || undefined
          if (get().contactID === next) {
            // Still mirror the shared key when store already matches (e.g. hydrate).
            persistSharedContactId(next ?? null)
            return
          }
          set({ contactID: next }, false, 'setContactID')
          persistSharedContactId(next ?? null)
          logger.log('setContactID', { contactID: next, source })
          if (source !== 'bridge') {
            widgetBridge.emit(IDENTITY_CONTACT_EVENT, { contactID: next ?? null })
          }
        },

        setReviewAuth: (auth: { token: string; expiresAt: string; pii: string }) => {
          set(
            {
              reviewToken: auth.token,
              reviewTokenExpiresAt: auth.expiresAt,
              reviewPII: auth.pii,
            },
            false,
            'setReviewAuth'
          )
          logger.log('setReviewAuth', { expiresAt: auth.expiresAt, pii: auth.pii })
        },

        clearReviewAuth: () => {
          set(
            { reviewToken: undefined, reviewTokenExpiresAt: undefined, reviewPII: undefined },
            false,
            'clearReviewAuth'
          )
        },

        fetchWidgetSettings: async (uid?: string, globalID?: string, menuType?: MenuType) => {
          const currentUID = uid ?? get().currentUID
          const currentGlobalID = globalID ?? get().currentGlobalID
          const currentMenuType = menuType ?? get().currentMenuType

          if (!currentUID) {
            logger.error('fetchWidgetSettings', 'No current UID set')
            return undefined
          }

          const isDemo = useWidgetStore.getState().demo

          const cached = widgetSettingsCache.get(isDemo, currentUID, currentGlobalID, currentMenuType)
          if (cached) {
            logger.log('fetchWidgetSettings', 'Using cached settings')
            set({ widgetSettings: cached }, false, 'fetchWidgetSettings/cached')
            const cachedZIndex = cached?.iconSettings?.designByBreakpoint?.all?.zIndex
            if (cachedZIndex != null) {
              widgetBridge.emit('settings:zIndex', { zIndex: cachedZIndex })
            }
            return cached
          }

          const [nextSettings, error] = await getWidgetSettingsAPI(
            currentUID ?? '',
            currentMenuType ?? 'both',
            {
              storeID: globalID ? getStoreIDByGlobalID(globalID) : undefined,
              merge: true,
              mergeUserSettings: true,
            }
          )

          if (error) {
            logger.error('fetchWidgetSettings', error)
            return undefined
          }

          if (nextSettings) nextSettings.globalID = currentGlobalID

          logger.log('fetchWidgetSettings', nextSettings)
          widgetSettingsCache.set(nextSettings!, currentUID, currentGlobalID, currentMenuType, isDemo)
          set(
            { widgetSettings: merge(getDefaultWidgetSettings(), nextSettings ?? undefined) },
            false,
            'fetchWidgetSettings'
          )
          const resolvedZIndex = nextSettings?.iconSettings?.designByBreakpoint?.all?.zIndex
          if (resolvedZIndex != null) {
            widgetBridge.emit('settings:zIndex', { zIndex: resolvedZIndex })
          }
          return nextSettings ?? undefined
        },

        fetchSignInState: async (uid?: string) => {
          const currentUID = uid ?? get().currentUID

          if (!currentUID) {
            logger.error('fetchSignInState', 'No current UID set')
            return undefined
          }

          const [signInState, error] = await getSignInState(currentUID)

          if (error) {
            logger.error('fetchSignInState', error)
            return undefined
          }

          set({ signInState: signInState ?? undefined }, false, 'fetchSignInState')
          return signInState ?? undefined
        },
      }),
      createPersistOptions<
        SessionStore,
        'currentUID' | 'currentGlobalID' | 'currentMenuType' | 'contactID' | 'ecomMenuProvider'
      >({
        name: 'session',
        pick: ['currentUID', 'currentGlobalID', 'currentMenuType', 'contactID', 'ecomMenuProvider'],
      })
    ),
    { name: 'SessionStore', enabled: process.env.NODE_ENV === 'development' }
  )
)

export function getContactID(): string | undefined {
  return useSessionStore.getState().contactID
}

export function getReviewToken(): string | undefined {
  return useSessionStore.getState().reviewToken
}

export function isReviewAuthenticated(): boolean {
  const { reviewToken, reviewPII } = useSessionStore.getState()
  return Boolean(reviewToken && reviewPII)
}

export function getReviewPII(): string | undefined {
  return useSessionStore.getState().reviewPII
}

export function getReviewTokenExpiresAt(): string | undefined {
  return useSessionStore.getState().reviewTokenExpiresAt
}

export function getCurrentGlobalID(): string | undefined {
  return useSessionStore.getState().currentGlobalID
}

export function getCurrentUID(): string | undefined {
  return useSessionStore.getState().currentUID
}

export function getInitialUIDs(): string[] {
  return useSessionStore.getState().initialUIDs
}

export function getCurrentMenuType(): MenuType | undefined {
  return useSessionStore.getState().currentMenuType
}

export function getEcomMenuProvider(): string | undefined {
  return useSessionStore.getState().ecomMenuProvider
}

export function getWidgetSettings(): WidgetSettings {
  const settings = useSessionStore.getState().widgetSettings
  const widgetSettings = merge(getDefaultWidgetSettings(), settings ?? {})
  return widgetSettings as WidgetSettings
}

export function getSignInStateData(): SignInStateResponse | undefined {
  return useSessionStore.getState().signInState
}

export function checkForMatchingUID(locations: LoyaltyLocation[]): Map<string, true> {
  const initialUIDs = getInitialUIDs()
  if (initialUIDs.length === 1) {
    const uid = initialUIDs[0]
    if (locations.some((location) => location.uid === uid)) {
      return new Map([[uid, true]])
    }
  }

  const matches: Map<string, true> = new Map()
  for (const location of locations) {
    const { uid, dynamicDisplayTargets } = location
    if (dynamicDisplayTargets) {
      if (checkUrlTargets(window.location.href, dynamicDisplayTargets)) {
        matches.set(uid, true)
      }
    }
  }
  return matches
}

/**
 * Scores how well a subEcomLocation's targets match the current URL.
 *
 * - Returns `null` if the sub does not match (excluded, or no include URL matched).
 * - Returns a `specificity` score where higher means a more specific match.
 *   Specificity is the length of the longest matching positive include URL
 *   (after normalization), or 0 when the only matches are negative conditions
 *   (`notEquals` / `notContains`) or there are no include URLs at all.
 *
 * This is what lets the matcher prefer `menu.xyz.com/med` (length 16) over
 * a more generic `menu.xyz.com` (length 12) when both technically match.
 */
function scoreSubLocationMatch(
  fullUrl: string,
  sub: SubEcomLocation
): { specificity: number } | null {
  const targets = sub.dynamicDisplayTargets
  if (!targets) return null

  for (const ex of targets.excludeURLs ?? []) {
    if (!ex.value || !ex.condition) continue
    if (UrlMatcher.matchesTarget(fullUrl, ex)) return null
  }

  const includeURLs = targets.includeURLs ?? []
  if (includeURLs.length === 0) {
    return { specificity: 0 }
  }

  let bestLength = -1
  for (const target of includeURLs) {
    if (!target.value || !target.condition) continue
    if (!UrlMatcher.matchesTarget(fullUrl, target)) continue

    if (target.condition === 'equals' || target.condition === 'contains') {
      const targetNorm = UrlMatcher.normalize(target.value)
      if (targetNorm.length > bestLength) {
        bestLength = targetNorm.length
      }
    } else if (bestLength < 0) {
      // negative-condition match keeps the sub eligible but contributes no specificity
      bestLength = 0
    }
  }

  if (bestLength < 0) return null
  return { specificity: bestLength }
}

/**
 * Finds the (globalID, menuType) pair whose URL targets best match the current page URL.
 *
 * Evaluates every (location, subEcomLocation) pair and picks the one with the
 * highest specificity score. Ties across different (globalID, menuType) pairs
 * return undefined so we don't silently pick the wrong store.
 *
 * Honors an embedded `globalID` or `?globalID=` query param as an unconditional
 * override.
 */
export function findBestMatchingGlobalID(
  locations: EcomLocation[],
  requiredUID?: string
): { globalID: string; uid: string; menuType: MenuType } | undefined {
  const embeddedConfig = getWidgetConfig()
  const queryParams = new URLSearchParams(window.location.search)
  const globalIDOverride = embeddedConfig?.globalID ?? queryParams.get('globalID')
  if (globalIDOverride) {
    const location = getEcomLocationByGlobalID(globalIDOverride, locations)
    if (location && (!requiredUID || location.uid === requiredUID)) {
      return {
        globalID: globalIDOverride,
        uid: location.uid,
        menuType: location.subEcomLocations[0]?.menuType,
      }
    }
  }

  const fullUrl = window.location.href
  let bestMatch: { globalID: string; uid: string; menuType: MenuType } | undefined
  let bestSpecificity = -1
  let tied = false

  for (const location of locations) {
    if (requiredUID && location.uid !== requiredUID) continue

    for (const sub of location.subEcomLocations) {
      const result = scoreSubLocationMatch(fullUrl, sub)
      if (!result) continue

      if (result.specificity > bestSpecificity) {
        bestSpecificity = result.specificity
        bestMatch = {
          globalID: location.globalID,
          uid: location.uid,
          menuType: sub.menuType,
        }
        tied = false
      } else if (
        result.specificity === bestSpecificity &&
        bestMatch &&
        (location.globalID !== bestMatch.globalID || sub.menuType !== bestMatch.menuType)
      ) {
        tied = true
      }
    }
  }

  return tied ? undefined : bestMatch
}

export function isRestrictedFlowEnabled(widgetSettings = getWidgetSettings()): boolean {
  const flowType = widgetSettings?.recommendationSettings?.flowType ?? RecommendationType.Default
  const settingEnabled = flowType === RecommendationType.Restricted
  return flowType === RecommendationType.Restricted && settingEnabled
}

/**
 * Checks if the currently selected store has multiple subEcomLocations,
 * meaning the user needs to pick a menu type.
 */
export function needsMenuTypeSelection(): boolean {
  const globalID = getCurrentGlobalID()
  if (!globalID) return false
  const location = getEcomLocationByGlobalID(globalID)
  if (!location) return false
  return location.subEcomLocations.length > 1
}
